import {
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Switch,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip
} from '@mui/material';
import { type User } from '../../../../auth';
import Log from '../../../../Logger';
import React, { useState, useEffect } from 'react';
import { SettingsOutlined, ErrorOutlineRounded } from '@mui/icons-material';
import { getTermsAndConditionsAgreement } from '../../util/insightReporter/functions/getTermsAndConditionsAgreement';
import { addTermsAndConditionsAgreement } from '../../util/insightReporter/functions/addTermsAndConditionsAgreement';
import TooltipIconButton from '../../util/insightReporter/tooltipIconButton';
import { checkVersion } from '../../util/insightReporter/functions/checkVersion';
import { INSIGHT_REPORTER_CONFIG, INSIGHT_REPORTER_LINKS } from '../../util/insightReporter/constant';

type Props = {
    user: User;
};

/**
 * AIレポート連携設定コンポーネント
 * 
 * テナント管理者がAIレポート機能の有効化/無効化を制御するための設定画面。
 * 利用規約への同意を必須とし、規約更新時には再同意を促す機能を提供する。
 * 
 * @component
 * @param {Props} props - コンポーネントのプロパティ
 * @param {User} props.user - 現在ログイン中のユーザー情報
 * 
 * @returns {JSX.Element} AIレポート連携設定のUI要素
 * 
 * @example
 * ```tsx
 * <AiReportSetting user={currentUser} />
 * ```
 * 
 * @description
 * 主な機能：
 * - AIレポート連携のオン/オフ切り替え
 * - 利用規約への同意確認ダイアログ
 * - 規約更新時の警告表示と再同意促進
 * - 利用規約リンクのクリック追跡
 * 
 * 状態管理：
 * - isLoading: データ読み込み状態
 * - agreementVersion: 現在同意済みの利用規約バージョン
 * - isSwitchOn: AIレポート連携の有効/無効状態
 * - isDialogOpen: 同意確認ダイアログの表示状態
 * - isCheckboxChecked: 同意チェックボックスの状態
 * - hasClickedTermsLink: 利用規約リンクのクリック状態
 */
const AiReportSetting: React.FC<Props> = ({ user }) => {
    const logger = Log.getLogger();

    // 状態管理
    const [isLoading, setIsLoading] = useState(true);
    const [agreementVersion, setAgreementVersion] = useState<string | undefined>(undefined);
    const [isSwitchOn, setIsSwitchOn] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [hasClickedTermsLink, setHasClickedTermsLink] = useState(false);
    const [hasClickedInfoLink, setHasClickedInfoLink] = useState(false);
    const [hasClickedAddOnTermsLink, setHasClickedAddOnTermsLink] = useState(false);

    // 利用規約取得
    useEffect(() => {
        const fetchAgreementVersion = async () => {
            try {
                const version = await getTermsAndConditionsAgreement(INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.NAME);
                setAgreementVersion(version);
            } catch (e) {
                console.log(e);
            }
        };
        fetchAgreementVersion();
    }, []);

    // スイッチ状態更新
    useEffect(() => {
        setIsLoading(false);
        setIsSwitchOn(!!agreementVersion);
    }, [agreementVersion]);

    // スイッチ操作
    const handleSwitchChange = async () => {
        if (!isSwitchOn) {
            // 有効化時はダイアログ表示
            setIsDialogOpen(true);
            setHasClickedTermsLink(false);
            setHasClickedInfoLink(false);
            setHasClickedAddOnTermsLink(false);
        } else {
            // 無効化時は規約解除
            await addTermsAndConditionsAgreement(INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.NAME, '');
            setIsSwitchOn(false);
            setAgreementVersion('');
        }
    };

    // ダイアログ「有効化する」クリック
    const handleDialogConfirm = async () => {
        await addTermsAndConditionsAgreement(INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.NAME, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.LATEST_VERSION);
        setIsSwitchOn(true);
        setAgreementVersion(INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.LATEST_VERSION);
        setIsDialogOpen(false);
    };

    // ダイアログ「キャンセル」クリック
    const handleDialogCancel = () => {
        setIsDialogOpen(false);
    };

    // 利用規約リンククリック
    const handleTermsLinkClick = () => {
        setHasClickedTermsLink(true);
    };

    const handleInfoLinkClick = () => {
        setHasClickedInfoLink(true);
    };

    const handleAddOnTermsLinkClick = () => {
        setHasClickedAddOnTermsLink(true);
    };

    // リスト項目定義
    const settingItem = {
        icon: <SettingsOutlined />,
        id: 'ai-report-setting',
        label: `${INSIGHT_REPORTER_CONFIG.NAME}機能を利用する`,
        state: isSwitchOn
    };

    return (
        <>
            {/* 設定リスト */}
            <List
                sx={{ bgcolor: 'background.paper' }}
                subheader={
                    <ListSubheader sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {INSIGHT_REPORTER_CONFIG.NAME}設定
                    </ListSubheader>
                }
            >
                <Box component='section' marginLeft={3} marginBottom={1}>
                    <Typography color='text.secondary' variant='body2'>
                        お客様テナント内の{INSIGHT_REPORTER_CONFIG.NAME}機能を管理します
                    </Typography>
                </Box>
                <Divider />
                {!isLoading && (
                    <ListItem key={settingItem.id} divider>
                        <ListItemIcon sx={{ color: 'inherit', marginLeft: '25px' }}>
                            {settingItem.icon}
                        </ListItemIcon>
                        <ListItemText id={'switch-list-label-' + settingItem.id} primary={settingItem.label} />
                        {/* 規約更新時の警告アイコン */}
                        {agreementVersion && !checkVersion(agreementVersion, INSIGHT_REPORTER_CONFIG.TERMS_AND_CONDITIONS.MINIMUM_VERSION) && (
                            <TooltipIconButton
                                isAvailable={true}
                                onPress={() => setIsDialogOpen(true)}
                                tooltipTitle={'利用規約が更新されました'}
                                icon={<ErrorOutlineRounded sx={{ display: 'block', color: 'red' }} />}
                            />
                        )}
                        <Switch
                            edge='start'
                            onChange={handleSwitchChange}
                            checked={settingItem.state}
                            disabled={isLoading}
                        />
                    </ListItem>
                )}
            </List>

            {/* 有効化ダイアログ */}
            <Dialog 
                open={isDialogOpen} 
                onClose={handleDialogCancel}
                PaperProps={{
                    sx: {
                        width: '45%',
                        maxWidth: 'none'
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>テナント内のAIレポート連携</DialogTitle>
                <DialogContent>
                    <Typography sx={{ ml: 2 }}>
                        規約およびご案内を確認・同意をしていただくことで<br />
                        テナント内のユーザーが{INSIGHT_REPORTER_CONFIG.NAME}機能を利用できる状態になります
                    </Typography>
                    <Box sx={{ mt: 2, mb: 2 }} />
                    <Typography sx={{ ml: 2 }}>
                        <span>
                            <a
                                href={INSIGHT_REPORTER_LINKS.TERMS}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' }}
                                onClick={handleTermsLinkClick}
                            >
                                本機能利用規約
                            </a>
                            <strong>（</strong>
                            <a
                                href={INSIGHT_REPORTER_LINKS.INFORMATION}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' }}
                                onClick={handleInfoLinkClick}
                            >
                                本機能に関するご案内
                            </a>
                            <strong>を含む）、<br />
                            および </strong>
                            <a
                                href={INSIGHT_REPORTER_LINKS.ADDON}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' }}
                                onClick={handleAddOnTermsLinkClick}
                            >
                                eWhiteboard Add-on Service 利用規約
                            </a>
                            <strong> に同意します。<br />
                            同意者は「所属企業等を代理して同意する権限を持っている」ことを表明します。</strong>
                        </span>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel}>キャンセル</Button>
                    <Box sx={{ display: 'inline-block' }}>
                        <Tooltip
                            title={!hasClickedAddOnTermsLink || !hasClickedInfoLink || !hasClickedTermsLink ? "全ての規約内容を確認してください" : ""}
                            arrow
                        >
                            <span>
                                <Button
                                    onClick={handleDialogConfirm}
                                    variant="contained"
                                    color="primary"
                                    disabled={!hasClickedAddOnTermsLink || !hasClickedInfoLink || !hasClickedTermsLink}
                                >
                                    同意する
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AiReportSetting;
